import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { FormField, ErrorMode, MarketingChannel, MarketingContext, CustomerProfile } from '@/models';
import { makeFormField, enforceMaxLength, isValidEmail, isValidPhoneNumber, stripNonNumerics, formatPhoneNumber, isValidCustomerName, isValidBirthday, stripLeadingOnesAndZeros, formatDate } from '@/helpers';
import moment from 'moment';
import { CustomerError, CustomerErrorCode } from '@/services/customer/customer.service';
import { AccountManagementEvent, AccountPageLoadEvent, ErrorEvent } from '@/services/analytics/events';
import { Routes } from '@/router';
import { isAutoSubscribesToLoyaltyEmail, isAutoSubscribesToMarketingEmail } from '@/helpers/country';
import { getI18nMultiLineContent } from '@/i18n';

interface EditAccountForm {
  [key: string]: FormField;
  firstName: FormField;
  lastName: FormField;
  telephone: FormField;
  email: FormField;
  birthday: FormField;
}

const EDIT_ACCOUNT_FORM: EditAccountForm = {
  firstName: makeFormField(),
  lastName: makeFormField(),
  telephone: makeFormField(),
  email: makeFormField(),
  country: makeFormField(),
  birthday: makeFormField(),
};

@Component({
  name: 'CmgEditAccountModal',
})
export default class CmgEditAccountModal extends Vue {
  editAccountForm = EDIT_ACCOUNT_FORM;
  isLoading = false;
  isFormValid = true;
  formSubmissionErrorMessage = '';
  countrySwitchMessageLines: string[] = [];

  @Watch('visible', { immediate: true, deep: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      await this.$services.customer.waitForCustomerInitialization();

      this.initializeForm();
    }
  }

  // On blur, the telephone number gets formatted, so
  // it requires max length of 14. When user is typing,
  // we want maxLength to be 10.
  get phoneInputMaxLength(): number {
    return this.form.telephone.formatted ? 14 : 10;
  }

  get customerProfile() {
    return this.$appStore.state.customer.profile;
  }

  get form(): EditAccountForm {
    return this.editAccountForm;
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.editAccountModal.visible;
  }

  get countrySwitchMessagePath(): string {
    const isEnrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customerProfile);
    const willAutoEnrollInLoyalty = isAutoSubscribesToLoyaltyEmail(this.form.country.value);

    let messagePath = 'modals.editAccount.countrySwitchMessage.' + 
                      (willAutoEnrollInLoyalty ? 'autoEnroll.' : 'nonAutoEnroll.') + 
                      (isEnrolledInLoyalty ? 'forLoyalty' : 'forNonLoyalty');

   return messagePath;
  }

  get isCountryChanged(): boolean {
    return this.customerProfile?.country !== this.form.country.value;
  }

  generateCountrySwitchMessageLines(): void {
    if (!this.customerProfile || !this.isCountryChanged) {
      this.countrySwitchMessageLines = [];
      return;
    }
    const fromCountryCode = this.customerProfile.country.toLocaleUpperCase();
    const toCountryCode = this.form.country.value.toLocaleUpperCase();

    const countryFrom = this.$t(`countries.${fromCountryCode}.shortName`);
    const countryTo = this.$t(`countries.${toCountryCode}.shortName`);
  
    const countryFromWithPrefixLastWord = this.$t(`countries.${fromCountryCode}.withPrefixShortNameLastWord`);
    const countryToWithPrefixLastWord = this.$t(`countries.${toCountryCode}.withPrefixShortNameLastWord`);
    
    this.countrySwitchMessageLines = getI18nMultiLineContent(this.countrySwitchMessagePath,{ countryFrom, countryTo, countryFromWithPrefixLastWord, countryToWithPrefixLastWord } );    
  }

  validateFirstName(): void {
    if (this.form.firstName.value.length == 0) {
      this.form.firstName.error = 'First Name Required';
      this.form.firstName.errorMode = ErrorMode.PlaceHolder;
    } else if (!isValidCustomerName(this.form.firstName.value)){
      this.form.firstName.error = 'No special characters allowed';
    } else {
      this.form.firstName.error = null;
    }
  }

  validateLastName(): void {
    if (this.form.lastName.value.length == 0) {
      this.form.lastName.error = 'Last Name Required';
    } else if (!isValidCustomerName(this.form.lastName.value)){
      this.form.lastName.error = 'No special characters allowed';
    } else {
      this.form.lastName.error = null;
    }

    this.form.lastName.errorMode = ErrorMode.PlaceHolder;
  }

  validateEmail(): void {
    if (this.form.email.value.length == 0) {
      this.form.email.error = 'Email Required';
    } else {
      this.form.email.error = !isValidEmail(this.form.email.value) ? 'Please Enter A Valid Email' : '';
    }

    this.form.email.errorMode = ErrorMode.PlaceHolder;
  }

  validateTelephoneNumber(): void {
    if (this.form.telephone.value.length == 0) {
      this.form.telephone.error = 'Phone Number Required';
    } else {
      this.form.telephone.error = !isValidPhoneNumber(this.form.telephone.value) ? 'Please Enter A Valid Phone Number' : '';
    }

    this.form.telephone.errorMode = ErrorMode.PlaceHolder;
  }

  sanitizePhoneNumber(value: string): string {
    return stripLeadingOnesAndZeros(stripNonNumerics(value));
  }

  stripNonNumerics(value: string): string {
    return stripNonNumerics(value);
  }

  formatPhoneNumber(value: string): string {
    return formatPhoneNumber(value);
  }

  formatBirthday(value: string): string {
    return formatDate(value);
  }

  validateCountry(): void {
    if (!this.form.country.value) {
      this.form.country.error = 'Please select a country';
    } else {
      this.form.country.error = '';
    }
  }

  validateBirthday(): void {
    // Validating MMDD
    this.form.birthday.error = !isValidBirthday(this.form.birthday.value) && this.form.birthday.value ? 'Please Enter A Valid Birthday MMDD' : '';

    this.form.birthday.errorMode = ErrorMode.PlaceHolder;
  }

  enforceMaxLength(value: string, validLengths: number[], formField: FormField): string {
    return enforceMaxLength(value, validLengths, formField);
  }

  onSelectCountry(value: string): void {
    this.form.country.error = '';
    this.form.country.dirty = true;
    this.form.country.value = value;
    this.generateCountrySwitchMessageLines();

    this.$services.analytics.send(new AccountPageLoadEvent('change-country'));
  }

  validateForm() {
    this.isFormValid = true;

    this.validateFirstName();
    this.validateLastName();
    this.validateEmail();
    this.validateTelephoneNumber();
    this.validateBirthday();
    this.validateCountry();

    for (const key of Object.keys(this.form)) {
      if (!!this.form[key].error) {
        this.form[key].dirty = true;
        this.isFormValid = false;
      }
    }
  }

  initializeForm(): void {
    this.isFormValid = true;
    this.formSubmissionErrorMessage = '';
    if (!this.customerProfile) {
      return;
    }

    this.form.firstName.value = this.customerProfile.firstName;
    this.form.lastName.value = this.customerProfile.lastName;
    this.form.email.value = this.customerProfile.email;

    this.form.telephone.value = this.customerProfile.phoneNumber;
    this.form.telephone.formatted = this.formatPhoneNumber(this.customerProfile.phoneNumber);

    this.form.country.value = this.customerProfile.country;
    this.form.birthday.value = !!this.customerProfile.dateOfBirth ? moment(this.customerProfile.dateOfBirth).format('MMDD') : '';
    this.form.birthday.formatted = !!this.customerProfile.dateOfBirth ? this.formatBirthday(moment(this.customerProfile.dateOfBirth).format('MMDD')) : '';
  }

  async onUpdateAccount(): Promise<void> {
    this.isLoading = true;
    this.formSubmissionErrorMessage = '';

    this.validateForm();

    if (!this.isFormValid || !this.customerProfile) {
      this.isLoading = false;
      return;
    }

    const birthday = this.form.birthday.value;

    const oldEmail = this.customerProfile.email;
    const newEmail = this.form.email.value;
    const changingEmail = oldEmail != newEmail;

    const hasEmailSubscription = this.$services.customer.hasSubscription(MarketingChannel.Email, MarketingContext.Marketing);
    const hasSmsSubscription = this.$services.customer.hasSubscription(MarketingChannel.Sms, MarketingContext.Marketing);
    const enrolledInLoyalty = this.$services.loyalty.isEnrolledInLoyalty(this.customerProfile);

    const oldPhoneNumber = this.customerProfile.phoneNumber;
    const newPhoneNumber = this.form.telephone.value;
    const changingPhoneNumber = oldPhoneNumber != newPhoneNumber;

    const oldCountry = this.customerProfile.country;
    const newCountry = this.form.country.value;
    const shouldAutoSubscribeToMarketingEmail = isAutoSubscribesToMarketingEmail(newCountry);
    const shouldAutoSubscribeToLoyaltyEmail = isAutoSubscribesToLoyaltyEmail(newCountry);
    const changingCountry = oldCountry !== newCountry;

    const sendUIDataToGigya = (enrolledInLoyalty && (changingEmail || changingCountry));

    // All the unsubscribing goes on here
    try {
      if (hasSmsSubscription && changingPhoneNumber) {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      }
      if (hasEmailSubscription && (changingEmail || changingCountry)) {
        await this.$services.customer.unsubscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      }
      if (enrolledInLoyalty && (changingEmail || changingCountry)) {
        await this.$services.loyalty.unenrollInLoyalty();
      }
      // Refreshing after unsubscribing seems allow us to cleanly subscribe again later.
      await this.$services.customer.refreshCustomerProfileInfo();
    } catch (err) {
      this.$services.logger.error('Failed to unsubscribe before updating customer.');
    }

    // Updating customer
    const customerPartial : any = {
      firstName: this.form.firstName.value,
      lastName: this.form.lastName.value,
      email: newEmail,
      phoneNumber: this.form.telephone.value,
      country: this.form.country.value,
      dateOfBirth: !!birthday ? `${birthday.slice(0, 2)}-${birthday.slice(2)}-1972` : '',
    }

    if(sendUIDataToGigya){
      customerPartial.hasSeenRewardsGenOnboarding = false;
      customerPartial.hasSeenRewardsExchOnboarding = false;
    }

    try {
      await this.$services.customer.updateCustomer(customerPartial);
    } catch (err) {
      this.$services.logger.error('failed to update customer');
      this.formSubmissionErrorMessage = err instanceof CustomerError ? err.message : 'Error trying to update customer';
      this.$services.analytics.send(new ErrorEvent('account', this.formSubmissionErrorMessage, 'edit-account'));

      if (err.errorCode === CustomerErrorCode.UpdateAccountEmailInUse) {
        this.$services.ui.setModalVisibility('verifyMobileNumber', false);
      } else if (err.errorCode === CustomerErrorCode.EmailUpdateRequiresTwoStepVerification || err.errorCode === CustomerErrorCode.PhoneNumberUpdateRequiresTwoStepVerification) {
        this.$services.ui.setModalVisibility('editAccountModal', false);
        this.$services.ui.setModalVisibility('verifyMobileNumber', true, { 
          email: this.$appStore.state.customer.profile?.email ?? '', 
          phoneNumber: newPhoneNumber ?? '', 
          requiresPasswordVerification: err.errorCode === CustomerErrorCode.PhoneNumberUpdateRequiresTwoStepVerification,
          requiresSignOutOnFailure: false,
          onSuccess: async () =>  { 
            await this.$services.customer.updateCustomer(customerPartial); 
            await this.$services.customer.refreshCustomerProfileInfo();

            if (changingPhoneNumber || changingEmail) {
              this.$services.ui.setModalVisibility('clearWalletModal', true);
            }
          },
          onFailure: async () => {
            if(sendUIDataToGigya) {
              await this.$services.customer.updateCustomer({ hasSeenRewardsGenOnboarding: false, hasSeenRewardsExchOnboarding: false });
            }
            this.$services.customer.signOut();
            this.$router.push({ name: Routes.Home });
          },
          onCancel: async () => {
            if(sendUIDataToGigya) {
              await this.$services.customer.updateCustomer({ hasSeenRewardsGenOnboarding: false, hasSeenRewardsExchOnboarding: false });
            }
            this.$services.ui.setModalVisibility('verifyMobileNumber', false);
            this.$services.ui.setModalVisibility('editAccountModal', true);
          },
        });
        Vue.nextTick(() => {
          this.$services.ui.focusDiv('.cmg-verify-mobile-number-modal');
        });
      }

      this.isLoading = false;
      return;
    }

    // Subscribing where necessary
    try {
      if (hasSmsSubscription && !changingPhoneNumber && changingCountry) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Sms, MarketingContext.Marketing);
      }
      if ((hasEmailSubscription && changingEmail && !changingCountry) || (changingCountry && shouldAutoSubscribeToMarketingEmail)) {
        await this.$services.customer.subscribeToMarketingChannel(MarketingChannel.Email, MarketingContext.Marketing);
      }
      if ((enrolledInLoyalty && changingEmail && !changingCountry) || (changingCountry && shouldAutoSubscribeToLoyaltyEmail)) {
        await this.$services.loyalty.enrollInLoyalty({
          customer: this.customerProfile,
          pageTitle: document.title,
          shouldSubscribeToMarketingEmail: false,
        });
      }
      await this.$services.customer.refreshCustomerProfileInfo();
    } catch (err) {
      this.$services.logger.error('Could not subscribe after updating customer');
    }

    this.$services.analytics.send(new AccountManagementEvent('personal-preferences|edit-account'));
    
    this.onClose();
    this.isLoading = false;
  }

  onClose() {
    this.initializeForm();
    this.$services.ui.setModalVisibility('editAccountModal', false);
    this.$services.ui.focusBack();
  }
}
